import {
    Alert,
    Button,
    Form,
    FormButtonRow,
    FormGroup,
    Heading,
    Text,
    TextInput,
    UserSelect,
} from '@ksyos/design-system';
import { useState } from 'react';
import { getSelectedLanguage, translateKey } from '@frontend/translate';
import type { IPractitioner } from '../../interfaces/practitioner';
import type { IUser } from './../../interfaces/user';
import { getDisplayInitials } from '../../../../common/utils/getDisplayInitials';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { practitionerEmailOnlySchema } from '../../../../common/validation/practitioner';
import { fetchJson } from '../../../store/fetch';
import { emptyValidationErrors, updateValidationErrorsForOne, validate } from '../../../utils/validation';
import { Figure } from '../../elements/figure/Figure';

export function ForgotPassword() {
    const [emailAddress, setEmailAddress] = useState('');
    const [validationErrors, setValidationErrors] = useState(emptyValidationErrors);
    const [resetEmailSent, setResetEmailSent] = useState(false);
    const [users, setUsers] = useState<IUser[]>([]);
    const [selectedPractitionerUserName, setSelectedPractitionerUserName] = useState<string | null>(null);
    const [practitionerNotFound, setPractitionerNotFound] = useState(false);

    function validateEmail() {
        setValidationErrors(
            updateValidationErrorsForOne(
                practitionerEmailOnlySchema,
                { emailAddress },
                validationErrors,
                'emailAddress'
            )
        );
    }

    async function handleSendEmail() {
        const result = await getPractitionersByEmail();
        if (result.length === 1) {
            sendEmail();
        } else {
            setUsers(
                result.map((practitioner: IPractitioner, index: number) => {
                    return {
                        id: index,
                        name: getDisplayName(practitioner),
                        initials: getDisplayInitials(practitioner),
                        username: practitioner.userName!,
                    };
                })
            );
        }
    }

    async function sendEmail() {
        const result = await fetchJson('/api/resetPassword', 'POST', {
            emailAddress,
            userName: selectedPractitionerUserName,
        });

        if (result.status === 404) {
            setPractitionerNotFound(true);
        } else {
            setResetEmailSent(true);
        }
    }

    async function getPractitionersByEmail(): Promise<IPractitioner[]> {
        setPractitionerNotFound(false);
        const practitioners: IPractitioner[] = [];
        const { hasErrors, errors } = validate(practitionerEmailOnlySchema, { emailAddress });
        setValidationErrors(errors);

        if (!hasErrors) {
            const result = await fetchJson('/api/findPractitionersByEmail/' + encodeURIComponent(emailAddress), 'GET');

            if (result.json.practitioners?.length > 0) {
                practitioners.push(...result.json.practitioners);
            } else {
                setPractitionerNotFound(true);
            }
        }

        return practitioners;
    }

    return (
        <>
            <Heading>Wachtwoord vergeten</Heading>
            {resetEmailSent ? (
                <ConfirmationStep emailAddress={emailAddress} />
            ) : users.length > 1 ? (
                <UserSelectStep
                    onSelect={setSelectedPractitionerUserName}
                    onSend={sendEmail}
                    practitionerNotFound={practitionerNotFound}
                    selectedUser={selectedPractitionerUserName}
                    users={users}
                />
            ) : (
                <EmailInputStep
                    emailAddress={emailAddress}
                    error={validationErrors.get('emailAddress')}
                    onBlur={validateEmail}
                    onEmailChange={setEmailAddress}
                    onSend={handleSendEmail}
                    practitionerNotFound={practitionerNotFound}
                />
            )}
        </>
    );
}

type EmailInputStepProps = {
    emailAddress: string;
    error?: string;
    onBlur: () => void;
    onEmailChange: (value: string) => void;
    onSend: () => void;
    practitionerNotFound: boolean;
};

function EmailInputStep({
    onBlur,
    onEmailChange,
    error,
    onSend,
    practitionerNotFound,
    emailAddress,
}: EmailInputStepProps) {
    const translate = getSelectedLanguage();

    return (
        <>
            <Text>
                Geen probleem. Vul je e-mailadres in, dan sturen we je een link via e-mail waarmee je je wachtwoord
                opnieuw kunt instellen.
            </Text>
            {practitionerNotFound && <WarningAlert />}
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSend();
                }}
                submitOnEnter={true}
            >
                <FormGroup
                    label="E-mailadres"
                    layout="vertical"
                >
                    <TextInput
                        error={error ? translateKey('validation.' + error) : undefined}
                        name="emailAddress"
                        onBlur={onBlur}
                        onChange={(e) => onEmailChange(e.target.value)}
                        type="text"
                        value={emailAddress}
                    />
                </FormGroup>
                <FormButtonRow>
                    <Button
                        color="secondary"
                        type="submit"
                        width="full"
                    >
                        {translate.common.next}
                    </Button>
                </FormButtonRow>
            </Form>
        </>
    );
}

type UserSelectStepProps = {
    onSelect: (value: string) => void;
    onSend: () => void;
    practitionerNotFound: boolean;
    selectedUser: string | null;
    users: IUser[];
};

function UserSelectStep({ onSelect, users, onSend, selectedUser, practitionerNotFound }: UserSelectStepProps) {
    const translate = getSelectedLanguage();

    return (
        <>
            <Text>
                Het e-mailadres dat je hebt opgegeven wordt gebruikt door meerdere Ksyos accounts. Selecteer jouw eigen
                account om het nieuwe wachtwoord voor in te stellen.
            </Text>
            {practitionerNotFound && <WarningAlert />}
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    onSend();
                }}
                submitOnEnter={true}
            >
                <FormGroup
                    label="Selecteer je account"
                    layout="vertical"
                >
                    <UserSelect
                        onChange={onSelect}
                        users={users}
                        value={selectedUser ?? ''}
                    />
                </FormGroup>
                <FormButtonRow>
                    <Button
                        color="secondary"
                        isDisabled={!selectedUser}
                        type="submit"
                        width="full"
                    >
                        {translate.common.next}
                    </Button>
                </FormButtonRow>
            </Form>
        </>
    );
}

type ConfirmationStepProps = {
    emailAddress: string;
};

function ConfirmationStep({ emailAddress }: ConfirmationStepProps) {
    return (
        <>
            <Text>
                Een e-mail is verstuurd naar <b>{emailAddress}</b>. Klik daar op de link om je wachtwoord opnieuw in te
                stellen.
            </Text>
            <Figure
                alt=""
                centered
                imageUrl="/assets/images/activatie.svg"
            />
        </>
    );
}

function WarningAlert() {
    return (
        <Alert
            appearance="warning"
            noMargin={true}
        >
            Dit e-mailadres is niet bij ons bekend. Neem contact met ons op via{' '}
            <Alert.Link
                href="tel:+31 20 600 00 60"
                variant="externalLink"
            >
                020-600 00 60
            </Alert.Link>{' '}
            of mail{' '}
            <Alert.Link
                href="mailto:info@ksyos.nl"
                variant="externalLink"
            >
                info@ksyos.nl
            </Alert.Link>
        </Alert>
    );
}
